/**
 * Charts Module
 *
 * Architecture:
 * - chartRegistry: Central registry of all available charts
 * - types: Common types and interfaces for charts
 * - ChartContainer: Wrapper component for consistent chart styling
 * - Individual chart components: Each chart in its own file
 *
 * To add a new chart:
 * 1. Create a new chart component file (e.g., MyNewChart.tsx)
 * 2. Import useBoardChartData hook to access board data
 * 3. Add chart type to CHART_TYPES in chartRegistry.tsx
 * 4. Add chart definition to CHART_DEFINITIONS in chartRegistry.tsx
 * 5. Add component mapping in chartRegistry.tsx
 */

// Core
export { ChartContainer } from './ChartContainer';
export { ChartLoader } from './ChartLoader';
export { ChartErrorBoundary } from './ChartErrorBoundary';
export * from './chartRegistry';
export * from './types';
export * from './chartStorage';

// Basic KPIs
export * from './TotalRevenueChart';
export * from './TotalExpensesChart';
export * from './NetProfitKPIChart';
export * from './OrdersKPIChart';
export * from './AdSpendKPIChart';
export * from './AOVKPIChart';

// Profitability
export * from './NetProfitOverTimeChart';
export * from './ProfitWaterfallChart';
export * from './ProfitMarginOverTimeChart';
export * from './IncomeVsCostsChart';
export * from './DailyPLTableChart';
export * from './RunningProfitChart';
export * from './ProfitPeriodComparisonChart';

// Ad Performance
export * from './ROASByPlatformChart';
export * from './ROASOverTimeChart';
export * from './BlendedMERChart';
export * from './AdSpendPercentChart';
export * from './CPAOverTimeChart';
export * from './CampaignROASLeaderboardChart';
export * from './AdSetEfficiencyScatterChart';
export * from './CreativePerformanceTableChart';
export * from './AdFunnelChart';
export * from './CPMTrendChart';
export * from './CPCTrendChart';
export * from './WastedAdSpendChart';
export * from './CTROverTimeChart';

// Product Performance
export * from './BestSellersByRevenueChart';
export * from './RevenueConcentrationChart';
export * from './ProductSalesTrendChart';
export * from './UnitsSoldRankingChart';
export * from './RevenuePerUnitChart';
export * from './ProductReturnRateChart';
export * from './RefundDamageByProductChart';
export * from './ProductChannelMatrixChart';

// Cost Breakdown
export * from './ExpenseCategoryDonutChart';
export * from './ExpenseCategoryTrendChart';
export * from './PaymentProcessingFeesChart';
export * from './PaymentFeesPercentChart';
export * from './ShippingCostOverTimeChart';
export * from './ShippingPerOrderChart';
export * from './DiscountTotalAndRateChart';
export * from './JournalExpenseBreakdownChart';
export * from './JournalExpensesOverTimeChart';
export * from './TrueCostPerOrderChart';
export * from './ExpenseAnomalyDetectorChart';

// Cash Flow
export * from './DailyInflowsOutflowsChart';
export * from './CumulativeCashPositionChart';
export * from './AdSpendVsRevenueTimingChart';
export * from './WeeklyCashSummaryChart';
export * from './BreakEvenDayChart';
export * from './MoneyFlowSankeyChart';

// Refunds
export * from './ReturnRateOverTimeChart';
export * from './RefundAmountOverTimeChart';
export * from './RefundTypeBreakdownChart';
export * from './RefundTrueCostChart';
export * from './MostReturnedProductsChart';

// Traffic & Attribution
export * from './PaidVsOrganicRevenueChart';
export * from './PaidVsOrganicTrendChart';
export * from './RevenueByUTMSourceChart';
export * from './RevenueByUTMCampaignChart';
export * from './UnattributedRevenueChart';

// Customer Insights
export * from './PurchaseFrequencyChart';
export * from './TopCustomersByRevenueChart';
export * from './CustomerConcentrationChart';
export * from './NewVsReturningRevenueChart';
export * from './AvgRevenuePerCustomerChart';

// Growth & Scaling
export * from './RevenuePeriodComparisonChart';
export * from './OrderVolumeOverTimeChart';
export * from './AOVTrendChart';
export * from './RevenueVsAdSpendGrowthChart';
export * from './SpendVsROASScatterChart';
export * from './SalesByDayOfWeekChart';

// Forecasting
export * from './RevenueForecastChart';
export * from './ExpenseForecastChart';
export * from './ProfitForecastChart';
export * from './BreakEvenProjectionChart';
export * from './SeasonalIndexChart';
export * from './WhatIfSimulatorChart';

// Campaign & Ad Changes
export * from './ActivityTimelineChart';
export * from './RevenueWithActionMarkersChart';
export * from './BudgetChangeImpactChart';
export * from './BudgetIncreaseEfficiencyChart';
export * from './CampaignToggleImpactChart';
export * from './PauseResumeComparisonChart';
export * from './CreativeSwapPerformanceChart';
export * from './AudienceChangeImpactChart';
export * from './AdApprovalTrackerChart';
export * from './CampaignLifecycleChart';

// Store & Product Changes
export * from './ProductLaunchPerformanceChart';
export * from './ProductRemovalImpactChart';
export * from './PromoDiscountRuleImpactChart';
export * from './RefundSpikeAnalysisChart';

// Operational Health
export * from './OrderFulfillmentFunnelChart';
export * from './CancellationRateOverTimeChart';
export * from './ActivityVolumeVsRevenueChart';
export * from './BillingIssuesTimelineChart';
export * from './ActionBreakdownByTypeChart';
export * from './BeforeAfterAnyActionChart';
